import { Grid } from '@material-ui/core';
import {
  Button,
  Card,
  Checkbox,
  FormControlLabel,
  i18n,
  Icons,
  Select,
  TextField,
  Typography,
} from '@nutrien/cxp-components';
import { useSiteFeatures } from '@nutrien/minesight-utility-module';
import dayjs from 'dayjs';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useFlags } from '@/featureFlags/useFlags';
import { useMst } from '@/mobx-models/Root';
import useEquipment, { PopulatedEquipment } from '@/rxdb/Equipment/useEquipment';
import { AssignmentType } from '@/utilities/constants';
import { formatInLocalTimezone } from '@/utilities/hooks/useDateFormatters';
import { useNotification } from '@/utilities/index';

import useStyles from './EquipmentTab.styles';
import ResultNotFound from './ResultNotFound';

const PAGE_SIZE = 20;

const EquipmentTab: React.FC = () => {
  const classes = useStyles();
  const { activeEquipmentList, assignedEquipmentList, updateEquipment } = useEquipment();
  const { equipmentTabEditable, equipmentLocation } = useFlags();
  const { equipment } = useMst();
  const { isRocanville } = useSiteFeatures();

  const [onlyAssignedEquipment, setOnlyAssignedEquipment] = useState(false);

  const equipmentList = useMemo(() => {
    return onlyAssignedEquipment ? assignedEquipmentList : activeEquipmentList;
  }, [activeEquipmentList, assignedEquipmentList, onlyAssignedEquipment]);

  const editIndex = equipment.editIndexValue;

  const [searchQuery, setSearchQuery] = useState('');
  const [filteredEquipment, setFilteredEquipment] = useState(equipmentList);
  const [loadedCount, setLoadedCount] = useState(PAGE_SIZE);

  const [visibleEquipment, setVisibleEquipment] = useState<PopulatedEquipment[]>(
    filteredEquipment.slice(0, PAGE_SIZE),
  );
  const [initialValues, setInitialValues] = useState<PopulatedEquipment | null>(null);

  const bottomRef = useRef<HTMLDivElement | null>(null);
  const { successNotification } = useNotification();

  const loadMoreData = useCallback(() => {
    if (loadedCount < filteredEquipment.length) {
      setTimeout(() => {
        setVisibleEquipment(prev => [
          ...prev,
          ...filteredEquipment.slice(loadedCount, loadedCount + PAGE_SIZE),
        ]);
        setLoadedCount(prev => prev + PAGE_SIZE);
      }, 200);
    }
  }, [filteredEquipment, loadedCount]);

  // Infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting) {
          loadMoreData();
        }
      },
      { threshold: 0.5 },
    );

    if (bottomRef.current) observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [loadMoreData]);

  // Filter equipment list when searchQuery changes
  useEffect(() => {
    const searchKeyword = searchQuery?.toLowerCase()?.trim();

    const filteredList = equipmentList.filter(equipmentData => {
      return (
        equipmentData.equipmentType?.description?.toLowerCase().includes(searchKeyword) ||
        equipmentData.shortName?.toLowerCase()?.includes(searchKeyword) ||
        equipmentData.longName?.toLowerCase()?.includes(searchKeyword)
      );
    });
    setFilteredEquipment(filteredList);
    setTimeout(() => {
      setVisibleEquipment(filteredList.slice(0, PAGE_SIZE));
      setLoadedCount(PAGE_SIZE);
    }, 200);
  }, [equipmentList, searchQuery]);

  const { statusOptions, assignmentOptions } = useMemo(() => {
    const uniqueStatuses = new Map();
    const uniqueAssignments: PopulatedEquipment[] = [];
    const unassignedAssignment: PopulatedEquipment = {
      id: null,
      shortName: 'Unassigned',
      longName: 'Unassigned',
    };

    uniqueAssignments.push(unassignedAssignment);

    activeEquipmentList.forEach((equipmentData: PopulatedEquipment) => {
      if (equipmentData.equipmentStatus?.id) {
        uniqueStatuses.set(equipmentData.equipmentStatus.id, equipmentData.equipmentStatus);
      }
      if (
        [AssignmentType.BORER, AssignmentType.REHAB_MACHINE].includes(
          equipmentData.equipmentType?.description.toLowerCase() as AssignmentType,
        )
      ) {
        uniqueAssignments.push(equipmentData);
      }
    });
    return {
      statusOptions: Array.from(uniqueStatuses.values()),
      assignmentOptions: uniqueAssignments,
    };
  }, [activeEquipmentList]);

  const handleUpdateEquipment = (index: number, updatedFields: Partial<PopulatedEquipment>) => {
    setVisibleEquipment(prev =>
      prev.map((equipmentData, i) => {
        return i === index
          ? {
              ...equipmentData,
              ...updatedFields,
              updatedAt: Date.now(),
              modifiedOn: dayjs().toISOString(),
            }
          : equipmentData;
      }),
    );
  };

  const handleSave = async (index: number) => {
    const updatedEquipment = visibleEquipment[index];
    await updateEquipment(updatedEquipment);
    successNotification('Changes saved');
    equipment.setEditIndex(null);
    setInitialValues(null);
  };

  const hasChanges = (index: number) => {
    const equipmentData = visibleEquipment[index];
    if (!initialValues) return false;
    return (
      equipmentData.equipmentStatusId !== initialValues.equipmentStatusId ||
      equipmentData.assignedBorerEquipmentId !== initialValues.assignedBorerEquipmentId ||
      equipmentData.comment !== initialValues.comment
    );
  };

  const handleEdit = (index: number) => {
    const equipmentVisible = visibleEquipment[index];
    setInitialValues(equipmentVisible);
    equipment.setEditIndex(index);
  };

  const handleOnSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e?.currentTarget?.value);
  };

  const handleOnAssignedCheck = () => {
    setOnlyAssignedEquipment(prev => !prev);
  };

  const handleCancel = (index: number) => {
    setVisibleEquipment(prev =>
      prev.map((equipmentData, i) => (i === index ? equipmentList[index] : equipmentData)),
    );
    equipment.setEditIndex(null);
    setInitialValues(null);
  };
  const clearFilterHandler = useCallback(() => {
    setSearchQuery('');
  }, []);

  return (
    <Card elevation={1} className={classes.card}>
      <Grid container>
        <Grid item container xs={12}>
          <Grid item className={classes.searchContainer}>
            <TextField
              className={classes.searchText}
              placeholder="Search"
              value={searchQuery}
              onChange={handleOnSearch}
              variant="outlined"
              size="small"
              noMinHeight
              startAdornment={<Icons.SearchFeather color="primary" style={{ marginRight: 3 }} />}
            />
            <Button
              className={classes.searchBtn}
              id="equipment-clear-search"
              data-testid="equipment-clear-search"
              color="primary"
              variant="outlined"
              noMinHeight
              onClick={clearFilterHandler}
              disabled={searchQuery.trim().length === 0}
            >
              {i18n.t('Clear search')}
            </Button>
          </Grid>
          <Grid item className={classes.assignedCheckboxGrid}>
            <FormControlLabel
              className={classes.assignedCheckbox}
              control={
                <Checkbox
                  onChange={handleOnAssignedCheck}
                  color="primary"
                  checked={onlyAssignedEquipment}
                  css={undefined}
                />
              }
              label="Show only assigned equipment"
            />
          </Grid>
        </Grid>
        <div className={classes.scrollableContainer}>
          <div className={classes.mainContainer}>
            <Grid className={classes.container} item container xs={12} md={12}>
              {equipmentList.length > 0 && filteredEquipment.length === 0 ? (
                <ResultNotFound clearFilterCallback={clearFilterHandler} />
              ) : (
                <>
                  <Grid
                    item
                    xs={equipmentLocation ? 3 : 5}
                    md={equipmentLocation ? 3 : 5}
                    className={classes.gridItemContainer}
                  >
                    <>
                      <Grid item xs={6} md={6} className={classes.gridItem}>
                        <Typography className={classes.header} variant="h6">
                          Type
                        </Typography>
                      </Grid>
                      <Grid xs={6} md={6} item className={classes.gridItem}>
                        <Typography className={classes.header} variant="h6">
                          Name
                        </Typography>
                      </Grid>
                    </>
                  </Grid>
                  {equipmentLocation && (
                    <Grid item xs={3} md={3} className={classes.gridItemContainer}>
                      <>
                        <Grid item xs={6} md={6} className={classes.gridItem}>
                          <Typography className={classes.header} variant="h6">
                            {isRocanville ? 'Ambra location' : 'Aeroscout location'}
                          </Typography>
                        </Grid>
                        <Grid item xs={6} md={6} className={classes.gridItem}>
                          <Typography className={classes.header} variant="h6">
                            Location time
                          </Typography>
                        </Grid>
                      </>
                    </Grid>
                  )}
                  <Grid
                    item
                    xs={equipmentLocation ? 6 : 7}
                    md={equipmentLocation ? 6 : 7}
                    className={classes.gridItemContainer}
                  >
                    <>
                      {equipmentTabEditable && (
                        <Grid
                          item
                          xs={2}
                          md={2}
                          className={`${classes.gridItemAction} ${classes.statusItemHeader}`}
                        >
                          <Typography className={classes.header} variant="h6">
                            Action
                          </Typography>
                        </Grid>
                      )}
                      <Grid item xs={2} md={2} className={classes.gridItem}>
                        <Typography className={classes.header} variant="h6">
                          Status
                        </Typography>
                      </Grid>
                      <Grid item xs={3} md={3} className={classes.gridItem}>
                        <Typography className={classes.header} variant="h6">
                          Assignment
                        </Typography>
                      </Grid>
                      <Grid
                        item
                        xs={equipmentTabEditable ? 5 : 7}
                        md={equipmentTabEditable ? 5 : 7}
                        className={classes.gridItem}
                      >
                        <Typography className={classes.header} variant="h6">
                          Comments
                        </Typography>
                      </Grid>
                    </>
                  </Grid>

                  {visibleEquipment.map((equipmentData, index: number) => (
                    <Grid
                      container
                      item
                      key={`${equipmentData.equipmentTypeId}-${index}`}
                      xs={12}
                      md={12}
                    >
                      <Grid
                        item
                        xs={equipmentLocation ? 3 : 5}
                        md={equipmentLocation ? 3 : 5}
                        className={classes.gridItemContainer}
                      >
                        <>
                          <Grid item xs={6} md={6} className={classes.gridItem}>
                            <Typography
                              className={`${classes.itemSize} ${classes.header}`}
                              fontWeight={600}
                            >
                              {equipmentData.equipmentType?.description || ''}
                            </Typography>
                          </Grid>
                          <Grid item xs={6} md={6} className={classes.gridItem}>
                            <Typography
                              className={`${classes.itemSize} ${classes.header}`}
                              fontWeight={600}
                            >
                              {equipmentData.longName || equipmentData.shortName}
                            </Typography>
                          </Grid>
                        </>
                      </Grid>
                      {equipmentLocation && (
                        <Grid item xs={3} md={3} className={classes.gridItem}>
                          <>
                            <Grid item xs={6} md={6} className={classes.gridItem}>
                              <Typography
                                className={`${classes.itemSize} ${classes.header}`}
                                fontWeight={600}
                              >
                                {equipmentData.lastKnownLocationDescription}
                              </Typography>
                            </Grid>

                            <Grid item xs={6} md={6} className={classes.gridItem}>
                              <Typography
                                className={`${classes.itemSize} ${classes.header}`}
                                fontWeight={600}
                              >
                                {equipmentData.lastLocationReportedDateTime &&
                                  formatInLocalTimezone(equipmentData.lastLocationReportedDateTime)}
                              </Typography>
                            </Grid>
                          </>
                        </Grid>
                      )}
                      <Grid
                        item
                        xs={equipmentLocation ? 6 : 7}
                        md={equipmentLocation ? 6 : 7}
                        className={classes.gridItemContainer}
                      >
                        <>
                          {equipmentTabEditable && (
                            <Grid
                              item
                              xs={2}
                              md={2}
                              className={`${classes.gridItemAction} ${classes.statusItem} ${
                                editIndex === index ? classes.selectedRow : ''
                              }`}
                            >
                              {editIndex === index ? (
                                <Grid
                                  item
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '60%',
                                  }}
                                >
                                  <Icons.CheckCircleIcon
                                    color={hasChanges(index) ? 'primary' : 'disabled'}
                                    onClick={() => {
                                      if (hasChanges(index)) handleSave(index);
                                    }}
                                  />
                                  <Icons.XCircleFeather
                                    color="error"
                                    onClick={() => handleCancel(index)}
                                  />
                                </Grid>
                              ) : (
                                <Icons.EditFeather
                                  color={
                                    editIndex === index || editIndex === null
                                      ? 'primary'
                                      : 'disabled'
                                  }
                                  onClick={editIndex === null ? () => handleEdit(index) : undefined}
                                />
                              )}
                            </Grid>
                          )}
                          <Grid
                            item
                            xs={2}
                            md={2}
                            className={`${classes.gridItem} ${
                              editIndex === index ? classes.selectedRow : ''
                            }`}
                          >
                            <Select
                              className={`${classes.statusSelect} ${classes.itemSize} ${
                                classes.header
                              } ${
                                editIndex !== index
                                  ? `${classes.readOnlyStatus} ${
                                      statusOptions
                                        .find(
                                          status => status.id === equipmentData.equipmentStatusId,
                                        )
                                        ?.description?.toLowerCase() === 'up'
                                        ? classes.up
                                        : classes.down
                                    }`
                                  : ''
                              } `}
                              variant={editIndex !== index ? 'empty' : 'outlined'}
                              list={statusOptions}
                              getOptionLabel={status => status.description}
                              getOptionValue={status => status}
                              onChange={(event, value) =>
                                handleUpdateEquipment(index, { equipmentStatusId: value.id })
                              }
                              value={statusOptions.find(
                                status => status.id === equipmentData.equipmentStatusId,
                              )}
                              data-testid={`Equipment-Status-${index}`}
                              css={undefined}
                              hideIconComponent={editIndex !== index ? true : false}
                            />
                          </Grid>

                          <Grid
                            item
                            xs={3}
                            md={3}
                            className={`${classes.gridItem} ${
                              editIndex === index ? classes.selectedRow : ''
                            }`}
                          >
                            <Select
                              className={`${classes.assignmentSelect} ${classes.itemSize} ${
                                classes.header
                              } ${editIndex !== index ? classes.readOnlyAssignment : ''} `}
                              variant={editIndex !== index ? 'empty' : 'outlined'}
                              list={assignmentOptions}
                              getOptionLabel={assignment =>
                                assignment.shortName || assignment.longName
                              }
                              getOptionValue={assignment => assignment}
                              onChange={(event: any, value: any) =>
                                handleUpdateEquipment(index, { assignedBorerEquipmentId: value.id })
                              }
                              value={
                                equipmentData.assignedBorerEquipmentId || editIndex === index
                                  ? assignmentOptions.find(
                                      assignment =>
                                        assignment.id === equipmentData.assignedBorerEquipmentId,
                                    )
                                  : null
                              }
                              data-testid={`Equipment-Assignment-${index}`}
                              css={undefined}
                              hideIconComponent={editIndex !== index ? true : false}
                            />
                          </Grid>

                          <Grid
                            item
                            xs={equipmentTabEditable ? 5 : 7}
                            md={equipmentTabEditable ? 5 : 7}
                            className={`${classes.gridItem} ${
                              editIndex === index ? classes.selectedRow : ''
                            }`}
                            style={{ paddingRight: '10px' }}
                          >
                            <TextField
                              className={`${classes.header} ${classes.comment} ${
                                editIndex !== index ? classes.readOnlyInput : ''
                              }`}
                              onChange={e =>
                                handleUpdateEquipment(index, { comment: e.target.value })
                              }
                              value={equipmentData.comment ?? ''}
                              data-cy="equipment-comment"
                              multiline
                              label={null}
                              InputLabelProps={{
                                shrink: true,
                                style: { height: 0, visibility: 'hidden', padding: 0, margin: 0 },
                              }}
                              readOnlyInput={editIndex !== index}
                              size="small"
                            />
                          </Grid>
                        </>
                      </Grid>
                    </Grid>
                  ))}
                  <Grid
                    item
                    container
                    ref={bottomRef}
                    data-testid="bottom-scroll"
                    style={{ height: '30px', width: '100%', marginBottom: '30px' }}
                  ></Grid>
                </>
              )}
            </Grid>
          </div>
        </div>
      </Grid>
    </Card>
  );
};

export default EquipmentTab;
